import {Validators} from '@angular/forms';

import {
    ReferralPayoutReportRequestModel,
    ReferralPayoutStatusEnum
} from '@spa/api';

import {ColorCodesEnum, ComponentTypesEnum} from '../../../app/shared/enums';
import {FilterControlInterface} from '../../../app/shared/interfaces';
import {
    BaseControl,
    CheckboxControl,
    DatepickerControl,
    SelectControl
} from '../../../app/shared/classes';
import {
    DialogButtonTypesEnum,
    DialogConfigInterface,
    ReportTableColumn,
    ReportTableConfigInterface,
    ReportTableData
} from '../../../app/shared/components';

/** Route param of the payout request detail page (`edit/:id`). */
export const PanelReferralPayoutRequestIdSelector = 'id';

/** Cache storage id of the payout requests queue (PanelPaymentsStorageId pattern). */
export const PanelReferralPayoutsStorageId = 'PanelReferralPayoutsComponent';

/** Queue filter control names — mapped onto ReferralPayoutReportRequestModel. */
export const PanelReferralPayoutStatusFilterSelector = ReferralPayoutReportRequestModel.getStatusSelector();
export const PanelReferralPayoutAffiliateFilterSelector = ReferralPayoutReportRequestModel.getSearchTextSelector();
export const PanelReferralPayoutDateTypeFilterSelector = 'dateType';
export const PanelReferralPayoutDateFromFilterSelector = 'dateFrom';
export const PanelReferralPayoutDateToFilterSelector = 'dateTo';
export const PanelReferralPayoutDateFilterSelector = 'filterDate';

/** "All" value of the status filter — every other value is a ReferralPayoutStatusEnum member. */
export const PanelReferralPayoutStatusFilterAll = 0;

/** Server pages 1–200 rows, default 50 (Report 2 contract). */
export const PanelReferralPayoutsDefaultItemsPerPage = 50;
export const PanelReferralPayoutsMaxItemsPerPage = 200;

/** Which report date column the From/To datepicker pair filters. */
export enum PanelReferralPayoutDateTypeEnum {
    Requested = 'requested',
    Approved = 'approved',
    Paid = 'paid'
}

/** Queue table selectors — the ReferralPayoutReportRowInterface field names. */
export const PanelReferralPayoutsColumnSelectors = {
    id: 'id',
    affiliateEmail: 'affiliate_email',
    requestedAt: 'requested_at',
    totals: 'totals_formatted',
    currency: 'currency',
    entryCount: 'entry_count',
    status: 'status',
    approvedAt: 'approved_at',
    paidAt: 'paid_at',
    paymentReference: 'payment_reference',
    approvedBy: 'approved_by_operator_user_id'
};

/** Decision dialog control names. */
export const PanelReferralPayoutPaymentReferenceSelector = 'paymentReference';
export const PanelReferralPayoutRejectionReasonSelector = 'rejectionReason';
export const PanelReferralPayoutCancellationReasonSelector = 'cancellationReason';
export const PanelReferralPayoutNoTransferConfirmationSelector = 'noTransferExecuted';

export const PanelReferralPayoutsFilterControls = (): FilterControlInterface[] => {
    return [
        {
            name: PanelReferralPayoutStatusFilterSelector,
            placeholder: 'Status',
            controls: [
                new SelectControl({
                    name: PanelReferralPayoutStatusFilterSelector,
                    value: PanelReferralPayoutStatusFilterAll,
                    placeholder: 'Status',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [
                        {
                            label: 'All',
                            value: PanelReferralPayoutStatusFilterAll
                        },
                        {
                            label: 'Requested',
                            value: ReferralPayoutStatusEnum.Requested
                        },
                        {
                            label: 'Approved',
                            value: ReferralPayoutStatusEnum.Approved
                        },
                        {
                            label: 'Paid',
                            value: ReferralPayoutStatusEnum.Paid
                        },
                        {
                            label: 'Rejected',
                            value: ReferralPayoutStatusEnum.Rejected
                        },
                        {
                            label: 'Cancelled',
                            value: ReferralPayoutStatusEnum.Cancelled
                        }
                    ]
                })
            ],
            shown: true,
            sortIndex: 1
        },
        {
            name: PanelReferralPayoutAffiliateFilterSelector,
            placeholder: 'Affiliate',
            controls: [
                new BaseControl({
                    name: PanelReferralPayoutAffiliateFilterSelector,
                    value: null,
                    placeholder: 'Affiliate (code or email)',
                    validators: [],
                    componentType: ComponentTypesEnum.Text
                })
            ],
            shown: true,
            sortIndex: 2
        },
        {
            name: PanelReferralPayoutDateTypeFilterSelector,
            placeholder: 'Date Type',
            controls: [
                new SelectControl({
                    name: PanelReferralPayoutDateTypeFilterSelector,
                    value: PanelReferralPayoutDateTypeEnum.Requested,
                    placeholder: 'Date Type',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [
                        {
                            label: 'Requested',
                            value: PanelReferralPayoutDateTypeEnum.Requested
                        },
                        {
                            label: 'Approved',
                            value: PanelReferralPayoutDateTypeEnum.Approved
                        },
                        {
                            label: 'Paid',
                            value: PanelReferralPayoutDateTypeEnum.Paid
                        }
                    ]
                })
            ],
            shown: true,
            sortIndex: 3
        },
        {
            name: PanelReferralPayoutDateFilterSelector,
            placeholder: 'Date',
            controls: [
                new DatepickerControl({
                    name: PanelReferralPayoutDateFromFilterSelector,
                    value: null,
                    placeholder: 'Date From',
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 1
                }),
                new DatepickerControl({
                    name: PanelReferralPayoutDateToFilterSelector,
                    value: null,
                    placeholder: 'Date To',
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 2
                }),
            ],
            shown: true,
            sortIndex: 4
        },
    ];
};

export const PanelReferralPayoutsDataTableItems = (): ReportTableData[] => {
    return [
        new ReportTableData<string>({
            colId: PanelReferralPayoutsColumnSelectors.id,
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: PanelReferralPayoutsColumnSelectors.affiliateEmail,
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: PanelReferralPayoutsColumnSelectors.requestedAt,
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: PanelReferralPayoutsColumnSelectors.totals,
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: PanelReferralPayoutsColumnSelectors.currency,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: PanelReferralPayoutsColumnSelectors.entryCount,
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: PanelReferralPayoutsColumnSelectors.status,
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: PanelReferralPayoutsColumnSelectors.approvedAt,
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: PanelReferralPayoutsColumnSelectors.paidAt,
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: PanelReferralPayoutsColumnSelectors.paymentReference,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: PanelReferralPayoutsColumnSelectors.approvedBy,
            value: null,
            hidden: false
        })
    ];
};

export const PanelReferralPayoutsDataTableConfig = (): ReportTableConfigInterface => {
    const columns = [
        new ReportTableColumn({
            label: 'Request ID',
            selector: PanelReferralPayoutsColumnSelectors.id,
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Affiliate',
            selector: PanelReferralPayoutsColumnSelectors.affiliateEmail,
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Date Requested',
            selector: PanelReferralPayoutsColumnSelectors.requestedAt,
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Total',
            // Computed JSON display field: EF cannot translate this sort to SQL.
            selector: PanelReferralPayoutsColumnSelectors.totals,
            sortable: false
        }),
        new ReportTableColumn({
            label: 'Currency',
            selector: PanelReferralPayoutsColumnSelectors.currency,
            sortable: false
        }),
        new ReportTableColumn({
            label: 'Line Items',
            selector: PanelReferralPayoutsColumnSelectors.entryCount,
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Status',
            selector: PanelReferralPayoutsColumnSelectors.status,
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Date Approved',
            selector: PanelReferralPayoutsColumnSelectors.approvedAt,
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Date Paid',
            selector: PanelReferralPayoutsColumnSelectors.paidAt,
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Payment Reference',
            selector: PanelReferralPayoutsColumnSelectors.paymentReference,
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Approved By',
            selector: PanelReferralPayoutsColumnSelectors.approvedBy,
            sortable: true
        })
    ];

    return {
        rows: [],
        columns,
        hiddenColumnIds: [],
        actionMenu: false
    };
};

/** Mark Paid dialog — the manually supplied payment reference is mandatory. */
export const PanelReferralPayoutPaymentReferenceControl = (): BaseControl => {
    return new BaseControl({
        name: PanelReferralPayoutPaymentReferenceSelector,
        value: null,
        placeholder: 'Payment reference',
        validators: [Validators.required],
        componentType: ComponentTypesEnum.Text
    });
};

/** Reject dialog — the reason is mandatory. */
export const PanelReferralPayoutRejectionReasonControl = (): BaseControl => {
    return new BaseControl({
        name: PanelReferralPayoutRejectionReasonSelector,
        value: null,
        placeholder: 'Reason',
        validators: [Validators.required],
        componentType: ComponentTypesEnum.Textarea
    });
};

/** Cancel approval dialog — the reason is mandatory. */
export const PanelReferralPayoutCancellationReasonControl = (): BaseControl => {
    return new BaseControl({
        name: PanelReferralPayoutCancellationReasonSelector,
        value: null,
        placeholder: 'Reason',
        validators: [Validators.required],
        componentType: ComponentTypesEnum.Textarea
    });
};

/** Cancel approval dialog — the operator must attest that no external transfer was executed. */
export const PanelReferralPayoutNoTransferConfirmationControl = (): CheckboxControl => {
    return new CheckboxControl({
        name: PanelReferralPayoutNoTransferConfirmationSelector,
        value: false,
        checked: false,
        placeholder: 'No transfer confirmation',
        validators: [],
        componentType: ComponentTypesEnum.Checkbox,
        color: ColorCodesEnum.Primary,
        expectBooleanValue: true
    });
};

export const PanelReferralPayoutApproveDialogConfig = (): DialogConfigInterface => {
    return {
        process: {
            loading: false,
            finished: false,
            failed: false,
            failedMessage: '',
            succeedMessage: ''
        },
        buttons: [
            {
                type: DialogButtonTypesEnum.Cancel,
                cssClass: 'btn-outline-secondary'
            },
            {
                type: DialogButtonTypesEnum.Confirm,
                customText: 'Approve'
            }
        ],
        buttonsDisabled: false,
        buttonsHidden: false
    };
};

export const PanelReferralPayoutMarkPaidDialogConfig = (): DialogConfigInterface => {
    return {
        process: {
            loading: false,
            finished: false,
            failed: false,
            failedMessage: '',
            succeedMessage: ''
        },
        buttons: [
            {
                type: DialogButtonTypesEnum.Cancel,
                cssClass: 'btn-outline-secondary'
            },
            {
                type: DialogButtonTypesEnum.Confirm,
                customText: 'Mark Paid'
            }
        ],
        buttonsDisabled: false,
        buttonsHidden: false
    };
};

export const PanelReferralPayoutRejectDialogConfig = (): DialogConfigInterface => {
    return {
        process: {
            loading: false,
            finished: false,
            failed: false,
            failedMessage: '',
            succeedMessage: ''
        },
        buttons: [
            {
                type: DialogButtonTypesEnum.Cancel,
                cssClass: 'btn-outline-secondary'
            },
            {
                type: DialogButtonTypesEnum.Confirm,
                customText: 'Reject request',
                cssClass: 'btn-danger'
            }
        ],
        buttonsDisabled: false,
        buttonsHidden: false
    };
};

export const PanelReferralPayoutCancelApprovalDialogConfig = (): DialogConfigInterface => {
    return {
        process: {
            loading: false,
            finished: false,
            failed: false,
            failedMessage: '',
            succeedMessage: ''
        },
        buttons: [
            {
                type: DialogButtonTypesEnum.Cancel,
                cssClass: 'btn-outline-secondary'
            },
            {
                type: DialogButtonTypesEnum.Confirm,
                customText: 'Cancel Approval',
                cssClass: 'btn-danger'
            }
        ],
        buttonsDisabled: false,
        buttonsHidden: false
    };
};
